import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { RowClickedEvent, SortChangedEvent } from 'ag-grid-community'

import { AgTable } from '@/components/primitives/ag-table'
import type { SkeletonShape } from '@/components/primitives/ag-table'
import { Empty } from '@/components/primitives/empty'
import type { PlantAssessment } from '@/features/assessments/api/assessment-list'
import type { User } from '@/features/assessments/api/users-list'
import { createAssessmentColumns } from './columns'

const PAGE_SIZE_OPTIONS = [20, 30, 50, 100]

type PaginationProps = {
  page: number
  pageSize: number
  totalCount: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

function buildPageNumbers(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages: (number | null)[] = [1]
  if (current > 3) pages.push(null)
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) pages.push(p)
  if (current < total - 2) pages.push(null)
  pages.push(total)
  return pages
}

function Pagination({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange
}: PaginationProps) {
  const totalPages = Math.ceil(totalCount / pageSize)
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = Math.min(page * pageSize, totalCount)
  const pageNumbers = buildPageNumbers(page, totalPages)

  return (
    <div className="flex items-center gap-3 text-xs text-gray-500">
      <span className="whitespace-nowrap">
        {rangeStart}–{rangeEnd} of {totalCount.toLocaleString()}
      </span>
      <div className="flex items-center gap-0.5">
        <button
          className="flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          ‹
        </button>
        {pageNumbers.map((p, i) =>
          p === null ? (
            <span
              key={`ellipsis-${i}`}
              className="flex h-6 w-5 items-center justify-center text-gray-400"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`flex h-6 min-w-[1.5rem] items-center justify-center rounded px-1 transition-colors ${
                p === page
                  ? 'bg-blue-600 font-medium text-white'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="flex h-6 w-6 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
      <select
        className="h-6 rounded border border-gray-200 bg-white px-1.5 text-xs text-gray-600 outline-none hover:border-gray-300"
        value={pageSize}
        onChange={e => onPageSizeChange(Number(e.target.value))}
        aria-label="Rows per page"
      >
        {PAGE_SIZE_OPTIONS.map(size => (
          <option key={size} value={size}>
            {size} / page
          </option>
        ))}
      </select>
    </div>
  )
}

type AssessmentTableProps = {
  rowData: PlantAssessment[]
  isLoading: boolean
  totalCount: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  onStartAssessment: (assessment: PlantAssessment) => void
  onEditAssessment: (assessment: PlantAssessment) => void
  onRowClicked?: (event: RowClickedEvent<PlantAssessment>) => void
  usersMap: Map<string, User>
  isUsersLoading: boolean
  hiddenCols: string[]
  multiPhase?: boolean
  syncedAssessmentIds?: ReadonlySet<string>
  footerRight?: ReactNode
  onSortChanged: (event: SortChangedEvent) => void
}

export function AssessmentTable({
  rowData,
  isLoading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onStartAssessment,
  onEditAssessment,
  onRowClicked,
  usersMap,
  isUsersLoading,
  hiddenCols,
  multiPhase = false,
  syncedAssessmentIds,
  footerRight,
  onSortChanged
}: AssessmentTableProps) {
  const { t } = useTranslation('plantAssessment')
  const isEmpty = !isLoading && rowData.length === 0

  const skeletonShapes: SkeletonShape[] = multiPhase
    ? ['icon-text', 'avatar-text', 'avatar-text', 'text', 'none']
    : ['icon-text', 'avatar-text', 'avatar-text', 'text', 'icon-text', 'none']

  const columnDefs = createAssessmentColumns({
    t,
    usersMap,
    isUsersLoading,
    hiddenCols,
    multiPhase,
    syncedAssessmentIds,
    onStartAssessment,
    onEditAssessment
  })

  return (
    <AgTable.Root>
      <AgTable.Content>
        {/*
          Keep a single Grid mounted across the loading→loaded transition. Swapping
          Skeleton and Grid as separate <AgGridReact> instances remounts the grid, and a
          freshly-mounted grid flashes ag-grid's built-in "No rows to show" overlay for a
          frame before rowData paints. We suppress that overlay (the app renders its own
          <Empty> instead) and overlay the Skeleton on top while loading.
        */}
        {isEmpty && (
          <AgTable.Empty>
            <Empty description="No assessments found" />
          </AgTable.Empty>
        )}
        <AgTable.Grid
          rowData={rowData}
          columnDefs={columnDefs}
          rowHeight={36}
          headerHeight={34}
          onSortChanged={onSortChanged}
          onRowClicked={onRowClicked}
          getRowClass={({ data }) =>
            data?.status === 'inProgress' || data?.status === 'completed' || data?.status === 'pending'
              ? 'cursor-pointer'
              : undefined
          }
          suppressCellFocus
          suppressRowClickSelection
          suppressNoRowsOverlay
        />
        {isLoading && (
          <div className="absolute inset-0 z-10 bg-white">
            <AgTable.Skeleton
              columnDefs={columnDefs}
              shapes={skeletonShapes}
              rowCount={10}
              rowHeight={36}
              headerHeight={34}
            />
          </div>
        )}
      </AgTable.Content>
      <AgTable.Footer className="justify-end">
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
        {footerRight}
      </AgTable.Footer>
    </AgTable.Root>
  )
}
